import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Banknote, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function PayoutMatchCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runCensus = async () => {
    setLoading(true);
    setData(null);
    try {
      const { data: result } = await api.get('/admin/diagnostics/payout-match-census', { timeout: 120000 });
      setData(result);
      const totalCovered = result.summary?.total_covered || 0;
      const totalBookings = result.summary?.total_bookings || 0;
      if (totalBookings === 0) {
        toast.info('No bookings found in scope');
      } else {
        const pct = ((totalCovered / totalBookings) * 100).toFixed(1);
        toast.success(`Census complete: ${pct}% payment coverage (${totalCovered}/${totalBookings})`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="payout-match-census-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Banknote className="h-5 w-5" />
          Payout Match Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Dry-run payout matching against real bank transactions. Reports per entity/channel/month
          match rates, tier breakdown, counterparty shapes, and payout-lag distribution.
        </p>

        <Button onClick={runCensus} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Banknote className="h-4 w-4 mr-2" />}
          Run Census
        </Button>

        {data && (
          <div className="space-y-4">
            {/* Payment Coverage Summary (replaces misleading bank-only rate) */}
            {data.summary && (
              <div className="flex items-center gap-2 text-sm" data-testid="payment-coverage-summary">
                {data.summary.coverage_rate >= 0.8 ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
                <span className="font-medium">
                  Payment coverage: {(data.summary.coverage_rate * 100).toFixed(1)}%
                </span>
                <span className="text-muted-foreground">
                  ({data.summary.total_covered}/{data.summary.total_bookings} bookings:
                  {' '}{data.summary.tier0_paid} PL-paid + {data.summary.fallback_matched} bank-matched)
                </span>
              </div>
            )}

            {/* Tier 0 (Pennylane) totals */}
            {data.tier0_totals && (
              <div data-testid="tier0-section">
                <h4 className="text-sm font-medium mb-2">Tier 0: Pennylane Invoice Coverage</h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div className="rounded border px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Covered</div>
                    <div className="font-mono font-medium">{data.tier0_totals.covered}</div>
                  </div>
                  <div className="rounded border px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Paid</div>
                    <div className="font-mono font-medium text-green-600 dark:text-green-400">{data.tier0_totals.paid}</div>
                  </div>
                  <div className="rounded border px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Partial</div>
                    <div className="font-mono font-medium text-amber-600 dark:text-amber-400">{data.tier0_totals.partially_paid}</div>
                  </div>
                  <div className="rounded border px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Unpaid</div>
                    <div className="font-mono font-medium text-red-600 dark:text-red-400">{data.tier0_totals.unpaid}</div>
                  </div>
                  <div className="rounded border px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Awaiting PL</div>
                    <div className="font-mono font-medium text-blue-600 dark:text-blue-400">{data.tier0_totals.awaiting_pl_reconciliation}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Discrepancy totals */}
            {data.discrepancy_totals && (data.discrepancy_totals.invoice_paid_no_bank_line > 0 || data.discrepancy_totals.bank_line_invoice_not_paid > 0) && (
              <div data-testid="discrepancy-section">
                <h4 className="text-sm font-medium mb-2 text-amber-600 dark:text-amber-400">Discrepancies</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div className="rounded border border-amber-200 dark:border-amber-800 px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">PL-paid, no bank line</div>
                    <div className="font-mono font-medium">{data.discrepancy_totals.invoice_paid_no_bank_line}</div>
                  </div>
                  <div className="rounded border border-amber-200 dark:border-amber-800 px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Bank line, PL-unpaid</div>
                    <div className="font-mono font-medium">{data.discrepancy_totals.bank_line_invoice_not_paid}</div>
                  </div>
                  <div className="rounded border border-amber-200 dark:border-amber-800 px-3 py-2 text-center">
                    <div className="text-xs text-muted-foreground">Awaiting PL recon</div>
                    <div className="font-mono font-medium">{data.discrepancy_totals.awaiting_pl_reconciliation}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Fallback (bank-line) tier totals */}
            {data.fallback_match_tier_totals && (
              <div data-testid="fallback-tier-section">
                <h4 className="text-sm font-medium mb-2">Fallback: Bank-Line Matching Tiers</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.fallback_match_tier_totals)
                    .filter(([, v]) => v > 0)
                    .map(([tier, count]) => (
                      <Badge key={tier} variant="outline" className="text-xs">
                        {tier}: {count}
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            {/* Entity summaries table (per row shows tier0 split) */}
            {data.entity_summaries && data.entity_summaries.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Entity / Channel / Month Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                        <th className="text-left px-2 py-1.5 font-medium">Channel</th>
                        <th className="text-left px-2 py-1.5 font-medium">Month</th>
                        <th className="text-right px-2 py-1.5 font-medium">Bookings</th>
                        <th className="text-right px-2 py-1.5 font-medium">T0 Paid</th>
                        <th className="text-right px-2 py-1.5 font-medium">T0 Partial</th>
                        <th className="text-right px-2 py-1.5 font-medium">T0 Awaiting</th>
                        <th className="text-right px-2 py-1.5 font-medium">Fallback Match</th>
                        <th className="text-right px-2 py-1.5 font-medium">Unmatched</th>
                        <th className="text-left px-2 py-1.5 font-medium">Tiers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entity_summaries.map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1.5">{row.entity_name || row.entity_id}</td>
                          <td className="px-2 py-1.5">{row.channel}</td>
                          <td className="px-2 py-1.5 font-mono">{row.month}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{row.booking_count}</td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {row.tier0?.paid > 0 ? (
                              <span className="text-green-600 dark:text-green-400">{row.tier0.paid}</span>
                            ) : (row.tier0?.paid ?? 0)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {row.tier0?.partially_paid > 0 ? (
                              <span className="text-amber-600 dark:text-amber-400">{row.tier0.partially_paid}</span>
                            ) : (row.tier0?.partially_paid ?? 0)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {row.tier0?.awaiting_pl_reconciliation > 0 ? (
                              <span className="text-blue-600 dark:text-blue-400">{row.tier0.awaiting_pl_reconciliation}</span>
                            ) : (row.tier0?.awaiting_pl_reconciliation ?? 0)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {row.fallback?.matched_count > 0 ? (
                              <span className="text-green-600 dark:text-green-400">{row.fallback.matched_count}</span>
                            ) : (row.fallback?.matched_count ?? 0)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {row.fallback?.unmatched_count > 0 ? (
                              <span className="text-red-600 dark:text-red-400">{row.fallback.unmatched_count}</span>
                            ) : (row.fallback?.unmatched_count ?? 0)}
                          </td>
                          <td className="px-2 py-1.5">
                            {row.fallback?.tier_breakdown && Object.entries(row.fallback.tier_breakdown)
                              .filter(([, v]) => v > 0)
                              .map(([tier, count]) => (
                                <Badge key={tier} variant="outline" className="text-[10px] mr-1">
                                  {tier}: {count}
                                </Badge>
                              ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Counterparty shape sample */}
            {data.counterparty_shapes && data.counterparty_shapes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Counterparty Shape Sample (Top 20 FR inbound)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Counterparty</th>
                        <th className="text-left px-2 py-1.5 font-medium">Description Snippet</th>
                        <th className="text-right px-2 py-1.5 font-medium">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.counterparty_shapes.map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1.5 font-mono">{row.counterparty || '-'}</td>
                          <td className="px-2 py-1.5 font-mono truncate max-w-xs">{row.description || '-'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payout-lag distribution */}
            {data.payout_lag_distribution && data.payout_lag_distribution.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Payout Lag Distribution (days after anchor)</h4>
                <div className="flex flex-wrap gap-2">
                  {data.payout_lag_distribution.map((bucket, i) => (
                    <div key={i} className="text-center px-3 py-1 bg-muted/50 rounded text-xs">
                      <div className="font-mono font-medium">{bucket.lag_days}d</div>
                      <div className="text-muted-foreground">{bucket.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scope funnel: per-filter breakdown */}
            {data.scope_breakdown && (
              <div data-testid="scope-funnel">
                <h4 className="text-sm font-medium mb-2">Scope Funnel (per collection)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Collection</th>
                        <th className="text-right px-2 py-1.5 font-medium">Total in period</th>
                        <th className="text-right px-2 py-1.5 font-medium">Status/ingestion filter</th>
                        <th className="text-right px-2 py-1.5 font-medium">No entity match</th>
                        <th className="text-right px-2 py-1.5 font-medium">Not in matching scope</th>
                        <th className="text-right px-2 py-1.5 font-medium">In scope</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.scope_breakdown).map(([coll, counts]) => (
                        <tr key={coll} className="border-b last:border-0">
                          <td className="px-2 py-1.5 font-mono">{coll.replace('_reservations', '')}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{counts.total_in_period}</td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {counts.excluded_by_filters > 0 ? (
                              <span className="text-amber-600 dark:text-amber-400">{counts.excluded_by_filters}</span>
                            ) : counts.excluded_by_filters}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {counts.excluded_by_entity_resolution > 0 ? (
                              <span className="text-amber-600 dark:text-amber-400">{counts.excluded_by_entity_resolution}</span>
                            ) : counts.excluded_by_entity_resolution}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {counts.excluded_by_matching_scope > 0 ? (
                              <span className="text-muted-foreground">{counts.excluded_by_matching_scope}</span>
                            ) : (counts.excluded_by_matching_scope ?? 0)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">{counts.in_scope}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Not in matching scope */}
            {data.not_in_matching_scope && data.not_in_matching_scope.length > 0 && (
              <div data-testid="not-in-scope-list">
                <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  Not in matching scope
                </h4>
                <div className="flex flex-wrap gap-1">
                  {data.not_in_matching_scope.map((e) => (
                    <Badge key={e.entity_id} variant="outline" className="text-[10px]">
                      {e.entity_name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
